import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './PurchaseEntries.css';
import CreateStockEntryModal from './CreateStockEntryModal';
import PurchaseOrderDetail from './PurchaseOrderDetail';
import DateRangePickerComponent from '../../../CommonComponents/DateRangePickerComponent';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Pagination } from '@mui/material';
import {
  FaCalendarAlt,
  FaFileAlt,
  FaFileInvoice,
  FaCoins,
  FaTruck,
  FaTag,
  FaFlag,
  FaPlus,
  FaDownload,
  FaSyncAlt,
  FaTimes,
  FaChevronDown,
  FaSearch,
  FaFileInvoiceDollar,
  FaArrowLeft,
  FaExclamationTriangle,
  FaInfoCircle,
  FaClock
} from 'react-icons/fa';

/**
 * PurchaseEntries Component
 * Viswateja Hospitals - Pharmacy Purchase & Stock Entries System
 * 100% In Sync with OP & IP Quality Standards:
 * 1. Top Sub-Tabs: Purchase Orders & Received Transfers
 * 2. Balanced Stat Cards (72px height, cyan left accent, centered values, arrows)
 * 3. Unified Master Toolbar: Fused Search (single arrow) + OP Standard Date Picker + Action CTAs
 * 4. Human-Readable Dates (e.g. "23 Sep 2026")
 * 5. Perfectly Aligned Column Headers & Values (8 columns, NO ACTIONS column)
 * 6. Zero clipping: All columns have generous width budgets
 * 7. Standard Master Pagination Footer
 */
const PurchaseEntries = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(location.state?.order || null);
  const [loadingOrder, setLoadingOrder] = useState(!location.state?.order && !!orderId);

  // ─── Sub-Tab State ('PURCHASE_ORDER' | 'RECEIVED_TRANSFER') ───────────────
  const [activeSubTab, setActiveSubTab] = useState('PURCHASE_ORDER');

  // ─── Date Range State ────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  // ─── Toolbar Fused Search (Single arrow, clean labels) ────────────────────
  const [searchCategory, setSearchCategory] = useState('invoiceNumber');
  const [searchQuery, setSearchQuery] = useState('');

  // ─── Data & Loading States ───────────────────────────────────────────────
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [metrics, setMetrics] = useState({
    totalPaymentAmount: 0,
    totalPurchaseEntries: 0,
    invoicedCount: 0,
    processedCount: 0,
    restockedCount: 0,
    deletedCount: 0,
    purchaseOrdersCount: 0,
    returnOrdersCount: 0
  });

  // ─── Suppliers List ──────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState([]);

  // ─── Column Filter Popovers State ─────────────────────────────────────────
  const [activePopover, setActivePopover] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    date: '',
    invoiceDate: '',
    grnNo: '',
    invoiceNumber: '',
    supplierName: '',
    type: 'ALL',
    status: 'ALL'
  });
  const filterBarRef = useRef(null);

  // ─── Modal State ─────────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [entryToEdit, setEntryToEdit] = useState(null);

  // ─── Download Dropdown State ─────────────────────────────────────────────
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const downloadDropdownRef = useRef(null);

  // ─── Pagination States ───────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // ─── Human-Readable Date Formatter (DD MMM YYYY) ──────────────────────────
  const formatDateReadable = (dateStr) => {
    if (!dateStr) return '—';
    const parts = String(dateStr).split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      if (!isNaN(monthIndex) && months[monthIndex]) {
        return `${String(day).padStart(2, '0')} ${months[monthIndex]} ${year}`;
      }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return dateStr;
  };

  // ─── Click Outside to Close Popovers & Dropdown ──────────────────────────
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setActivePopover(null);
      }
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(e.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // ─── Fetch Active Suppliers for Dropdown ──────────────────────────────────
  // ─── Sync Route Param with Selected Purchase Order ─────────────────────────
  // ─── Sync Route Param with Selected Purchase Order (Exact Appointments pattern)
  useEffect(() => {
    if (!orderId) {
      setSelectedPurchaseOrder(null);
      setLoadingOrder(false);
      return;
    }

    if (location.state?.order) {
      setSelectedPurchaseOrder(location.state.order);
      setLoadingOrder(false);
      return;
    }

    const decoded = decodeURIComponent(orderId).trim();
    let isCancelled = false;
    setLoadingOrder(true);

    const fetchOrderDirectly = async () => {
      try {
        if (/^\d+$/.test(decoded)) {
          try {
            const res = await axios.get(ENDPOINTS.GET_PURCHASE_ENTRY_BY_ID(decoded));
            if (!isCancelled && res.data) {
              setSelectedPurchaseOrder(res.data);
              setLoadingOrder(false);
              return;
            }
          } catch (e) {
            // fallback to search
          }
        }

        const resList = await axios.get(ENDPOINTS.GET_ALL_PURCHASE_ENTRIES, {
          params: { filterGrnNo: decoded, size: 50 }
        });
        const list = Array.isArray(resList.data) ? resList.data : resList.data?.content || [];
        const match = list.find((e) =>
          String(e.id) === decoded ||
          String(e.grnNo || '').toLowerCase() === decoded.toLowerCase() ||
          String(e.invoiceNumber || '').toLowerCase() === decoded.toLowerCase()
        ) || (list.length === 1 ? list[0] : null);

        if (!isCancelled) {
          setSelectedPurchaseOrder(match || null);
          setLoadingOrder(false);
        }
      } catch (err) {
        console.error('Failed to load purchase order', err);
        if (!isCancelled) {
          setSelectedPurchaseOrder(null);
          setLoadingOrder(false);
        }
      }
    };

    fetchOrderDirectly();

    return () => {
      isCancelled = true;
    };
  }, [orderId, location.state]);

  useEffect(() => {
    axios
      .get(ENDPOINTS.GET_ALL_SUPPLIERS, { params: { size: 500 } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
        setSuppliers(list);
      })
      .catch((err) => console.warn('Could not fetch suppliers:', err));
  }, []);

  const formatDateToYMD = (d) => {
    if (!d) return '';
    const dateObj = new Date(d);
    if (isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // ─── Fetch Entries & Metrics from Backend ─────────────────────────────────
  const fetchPurchaseData = async (isInitial = false) => {
    if (isInitial || entries.length === 0) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      const fromStr = startDate ? formatDateToYMD(startDate) : '';
      const toStr = endDate ? formatDateToYMD(endDate) : '';

      const entriesRes = await axios.get(ENDPOINTS.GET_ALL_PURCHASE_ENTRIES, {
        params: {
          fromDate: fromStr,
          toDate: toStr,
          searchCategory,
          searchQuery: searchQuery || undefined,
          filterDate: columnFilters.date || undefined,
          filterInvoiceDate: columnFilters.invoiceDate || undefined,
          filterGrnNo: columnFilters.grnNo || undefined,
          filterInvoiceNumber: columnFilters.invoiceNumber || undefined,
          filterSupplierName: columnFilters.supplierName || undefined,
          filterType: columnFilters.type !== 'ALL' ? columnFilters.type : undefined,
          filterStatus: columnFilters.status !== 'ALL' ? columnFilters.status : undefined,
          page: 0,
          size: 500,
          sortBy: 'entryDate',
          sortDirection: 'desc'
        }
      });

      const list = Array.isArray(entriesRes.data)
        ? entriesRes.data
        : entriesRes.data?.content || [];
      setEntries(list);

      const metricsRes = await axios.get(ENDPOINTS.GET_PURCHASE_ENTRIES_METRICS, {
        params: {
          fromDate: fromStr,
          toDate: toStr
        }
      });
      if (metricsRes.data) {
        setMetrics(metricsRes.data);
      }
    } catch (err) {
      console.error('Error fetching purchase data:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPurchaseData(false);
  }, [
    startDate,
    endDate,
    searchCategory,
    searchQuery,
    columnFilters.date,
    columnFilters.invoiceDate,
    columnFilters.grnNo,
    columnFilters.invoiceNumber,
    columnFilters.supplierName,
    columnFilters.type,
    columnFilters.status
  ]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchPurchaseData();
  };

  const handleRowClick = (entry) => {
    const poNumber = entry.grnNo || entry.id;
    setSelectedPurchaseOrder(entry);
    setSelectedEntry(entry);
    navigate(`/pharmacy/purchase-entries/${encodeURIComponent(poNumber)}`, {
      state: { order: entry }
    });
  };

  const handleDateChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  }, []);

  const handleDownloadCSV = (mode = 'standard') => {
    if (!entries || entries.length === 0) {
      toast.info('No data to download.');
      return;
    }

    if (mode === 'gst') {
      // Export with GST details (Taxable Value, GST %, CGST, SGST, Total Amount)
      const headers = [
        'Entry Date',
        'Invoice Date',
        'GRN No',
        'Invoice Number',
        'Supplier Name',
        'Supplier GSTIN',
        'Taxable Amount (Rs)',
        'GST Rate',
        'CGST (Rs)',
        'SGST (Rs)',
        'Total Amount with GST (Rs)',
        'Cleared Amount (Rs)',
        'Pending Amount (Rs)',
        'Type',
        'Status'
      ];

      const rows = entries.map((e) => {
        const supplierObj = suppliers.find((s) => s.id === e.supplierId || s.name === e.supplierName);
        const gstNum = supplierObj?.gstNumber || 'N/A';
        const totalAmt = parseFloat(e.amount || 0);
        // Standard pharma GST calculation (assuming 12% GST = 6% CGST + 6% SGST)
        const taxable = totalAmt / 1.12;
        const cgst = taxable * 0.06;
        const sgst = taxable * 0.06;

        return [
          formatDateReadable(e.entryDate) || '',
          formatDateReadable(e.invoiceDate) || '',
          e.grnNo || '',
          e.invoiceNumber || '',
          e.supplierName || '',
          gstNum,
          taxable.toFixed(2),
          '12%',
          cgst.toFixed(2),
          sgst.toFixed(2),
          totalAmt.toFixed(2),
          parseFloat(e.clearedAmount || 0).toFixed(2),
          parseFloat(e.pendingAmount || 0).toFixed(2),
          e.entryType === 'RETURN_ORDER' ? 'Return Order' : 'Purchase Order',
          e.statusDisplay || (
            e.status === 'INVOICED' ? 'Invoiced' :
              (e.status === 'PROCESSED_AND_RESTOCKED' || e.status === 'PROCESSED' || e.status === 'RESTOCKED' || e.status === 'PROCESSED AND RESTOCKED') ? 'Processed and Restocked' :
                e.status === 'DELETED' ? 'Deleted' : e.status || ''
          )
        ];
      });

      const csvContent =
        '\uFEFF' +
        [headers.join(','), ...rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `Purchase_Entries_With_GST_${new Date().toISOString().split('T')[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Standard Purchase Entries export
      const headers = [
        'Entry Date',
        'Invoice Date',
        'GRN No',
        'Invoice Number',
        'Supplier Name',
        'Amount (Rs)',
        'Cleared Amount (Rs)',
        'Pending Amount (Rs)',
        'Type',
        'Status'
      ];

      const rows = entries.map((e) => [
        formatDateReadable(e.entryDate) || '',
        formatDateReadable(e.invoiceDate) || '',
        e.grnNo || '',
        e.invoiceNumber || '',
        e.supplierName || '',
        parseFloat(e.amount || 0).toFixed(2),
        parseFloat(e.clearedAmount || 0).toFixed(2),
        parseFloat(e.pendingAmount || 0).toFixed(2),
        e.entryType === 'RETURN_ORDER' ? 'Return Order' : 'Purchase Order',
        e.statusDisplay || (
          e.status === 'INVOICED' ? 'Invoiced' :
            (e.status === 'PROCESSED_AND_RESTOCKED' || e.status === 'PROCESSED' || e.status === 'RESTOCKED' || e.status === 'PROCESSED AND RESTOCKED') ? 'Processed and Restocked' :
              e.status === 'DELETED' ? 'Deleted' : e.status || ''
        )
      ]);

      const csvContent =
        '\uFEFF' +
        [headers.join(','), ...rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `Purchase_Entries_${new Date().toISOString().split('T')[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // ─── Client-side Pagination ───────────────────────────────────────────────
  const totalPages = Math.ceil(entries.length / pageSize) || 1;
  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return entries.slice(start, start + pageSize);
  }, [entries, currentPage, pageSize]);

  // Dynamic calculated totals
  const activeTotalPaymentAmount = useMemo(() => {
    if (metrics?.totalPaymentAmount != null && metrics.totalPaymentAmount > 0) {
      return Number(metrics.totalPaymentAmount);
    }
    return entries.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
  }, [metrics, entries]);

  const activeTotalEntriesCount = useMemo(() => {
    if (metrics?.totalPurchaseEntries != null && metrics.totalPurchaseEntries > 0) {
      return metrics.totalPurchaseEntries;
    }
    return entries.length;
  }, [metrics, entries]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(val || 0);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      Boolean(searchQuery) ||
      Boolean(columnFilters.date) ||
      Boolean(columnFilters.invoiceDate) ||
      Boolean(columnFilters.grnNo) ||
      Boolean(columnFilters.invoiceNumber) ||
      Boolean(columnFilters.supplierName) ||
      columnFilters.type !== 'ALL' ||
      columnFilters.status !== 'ALL'
    );
  }, [searchQuery, columnFilters]);

  const clearSingleFilter = (field) => {
    if (field === 'search') {
      setSearchQuery('');
    } else {
      setColumnFilters((prev) => ({
        ...prev,
        [field]: field === 'type' || field === 'status' ? 'ALL' : ''
      }));
    }
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setColumnFilters({
      date: '',
      invoiceDate: '',
      grnNo: '',
      invoiceNumber: '',
      supplierName: '',
      type: 'ALL',
      status: 'ALL'
    });
    setActivePopover(null);
    setCurrentPage(1);
  };

  // If orderId is in URL, render detail view, loading, or not found (Exact Appointments pattern)
  if (orderId) {
    if (loadingOrder) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          Loading purchase order data...
        </div>
      );
    }

    if (selectedPurchaseOrder) {
      return (
        <PurchaseOrderDetail
          order={selectedPurchaseOrder}
          onBack={() => {
            navigate('/pharmacy/purchase-entries');
          }}
          onUpdate={(updatedOrder) => {
            if (updatedOrder) {
              setSelectedPurchaseOrder((prev) => ({ ...prev, ...updatedOrder }));
            }
            fetchPurchaseData(false);
          }}
          suppliers={suppliers}
        />
      );
    }

    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        Purchase order not found.
      </div>
    );
  }

  return (
    <div className="vt-pe-container">
      {/* ─── Stat Cards with Centered Content ─────────────────────────────── */}
      <div className="vt-pe-cards-row">
        {/* Card 1: Total Payment Amount */}
        <div className="vt-pe-stat-card">
          <div className="vt-pe-card-accent blue-accent" />
          <div className="vt-pe-card-icon blue-theme">
            <FaCalendarAlt size={18} />
          </div>
          <div className="vt-pe-card-content">
            <div className="vt-pe-card-val">{formatCurrency(activeTotalPaymentAmount)}</div>
            <div className="vt-pe-card-lbl">Total Payment Amount</div>
          </div>
        </div>

        {/* Card 2: Total Purchase Entries */}
        <div className="vt-pe-stat-card">
          <div className="vt-pe-card-accent purple-accent" />
          <div className="vt-pe-card-icon purple-theme">
            <FaCalendarAlt size={18} />
          </div>
          <div className="vt-pe-card-content">
            <div className="vt-pe-card-val">{activeTotalEntriesCount}</div>
            <div className="vt-pe-card-lbl">Total Purchase Entries</div>
          </div>
        </div>
      </div>

      {/* ─── 3. Unified Master Card (OP & IP Screens Standard) ───────────── */}
      <div className="unified-master-card">
        <div className="master-card-accent-bar" />

        {/* Master Toolbar Header: Fused Search + OP Calendar + CTAs */}
        <div className="master-toolbar-header">
          <div className="toolbar-left-group">
            {/* Seamless Fused Search (Clean single arrow) */}
            <div className="toolbar-search-box">
              <div className="fused-search-group">
                <select
                  value={searchCategory}
                  onChange={(e) => {
                    setSearchCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="invoiceNumber">Invoice Number</option>
                  <option value="grnNo">GRN No</option>
                  <option value="supplier">Supplier</option>
                  <option value="all">All Fields</option>
                </select>

                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder={
                      searchCategory === 'invoiceNumber'
                        ? 'Enter Invoice Number...'
                        : searchCategory === 'grnNo'
                          ? 'Enter GRN No...'
                          : searchCategory === 'supplier'
                            ? 'Enter Supplier Name...'
                            : 'Search entries...'
                    }
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* OP Standard Date Picker (Exact IP/OP Screen component & styling) */}
            <div
              className="toolbar-date-picker"
              onClick={(e) => {
                const inputEl = e.currentTarget.querySelector('input');
                if (inputEl && e.target !== inputEl) {
                  inputEl.focus();
                  inputEl.click();
                }
              }}
            >
              <DateRangePickerComponent
                initialStartDate={startDate}
                initialEndDate={endDate}
                onDateChange={handleDateChange}
              />
            </div>
          </div>

          {/* Action CTAs Right */}
          <div className="toolbar-right-group">
            {hasActiveFilters && (
              <button
                type="button"
                className="master-secondary-btn reset-btn"
                onClick={handleClearAllFilters}
                title="Reset active column filters"
              >
                <FaTimes size={11} /> Reset
              </button>
            )}

            {/* Refresh Icon Button (No text, sleek tooltip & icon) */}
            <button
              type="button"
              className="master-toolbar-icon-btn"
              onClick={handleRefresh}
              title="Refresh entries"
              aria-label="Refresh entries"
            >
              <FaSyncAlt className={isRefreshing ? 'vt-spinning' : ''} size={15} />
            </button>

            {/* Download Icon Button with Dropdown (Purchase entries / Purchase entries with gst) */}
            <div className="download-dropdown-container" ref={downloadDropdownRef}>
              <button
                type="button"
                className={`master-toolbar-icon-btn download-trigger-btn ${isDownloadOpen ? 'active' : ''}`}
                onClick={() => setIsDownloadOpen((prev) => !prev)}
                title="Download options"
                aria-label="Download options"
              >
                <FaDownload size={14} />
                <FaChevronDown size={9} className="download-chevron" />
              </button>

              {isDownloadOpen && (
                <div className="download-dropdown-menu">
                  <div className="download-dropdown-header">Export Options</div>
                  <button
                    type="button"
                    className="download-dropdown-item"
                    onClick={() => {
                      handleDownloadCSV('standard');
                      setIsDownloadOpen(false);
                    }}
                  >
                    <div className="download-item-icon standard">
                      <FaFileAlt size={14} />
                    </div>
                    <div className="download-item-info">
                      <span className="download-item-title">Purchase entries</span>
                      <span className="download-item-sub">Standard CSV format</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    className="download-dropdown-item"
                    onClick={() => {
                      handleDownloadCSV('gst');
                      setIsDownloadOpen(false);
                    }}
                  >
                    <div className="download-item-icon gst">
                      <FaFileInvoiceDollar size={15} />
                    </div>
                    <div className="download-item-info">
                      <span className="download-item-title">Purchase entries with gst</span>
                      <span className="download-item-sub">Includes GSTIN, CGST & SGST</span>
                    </div>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className="master-new-appt-btn"
              onClick={() => {
                setEntryToEdit(null);
                setIsModalOpen(true);
              }}
              title="Create a new stock/purchase entry"
            >
              <FaPlus size={12} /> Create Stock Entry
            </button>
          </div>
        </div>

        {/* Active Filter Chips Banner (Exact Suppliers Screen Architecture) */}
        {hasActiveFilters && (
          <div className="pe-active-filters-chips">
            <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
              Active Filters:
            </span>
            {searchQuery && (
              <span className="pe-filter-chip">
                Search: "{searchQuery}"
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('search')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {columnFilters.date && (
              <span className="pe-filter-chip">
                Entry Date: {formatDateReadable(columnFilters.date)}
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('date')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {columnFilters.invoiceDate && (
              <span className="pe-filter-chip">
                Invoice Date: {formatDateReadable(columnFilters.invoiceDate)}
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('invoiceDate')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {columnFilters.grnNo && (
              <span className="pe-filter-chip">
                GRN No: {columnFilters.grnNo}
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('grnNo')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {columnFilters.invoiceNumber && (
              <span className="pe-filter-chip">
                Invoice Number: {columnFilters.invoiceNumber}
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('invoiceNumber')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {columnFilters.supplierName && (
              <span className="pe-filter-chip">
                Supplier: {columnFilters.supplierName}
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('supplierName')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {columnFilters.type !== 'ALL' && (
              <span className="pe-filter-chip">
                Type: {columnFilters.type}
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('type')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {columnFilters.status !== 'ALL' && (
              <span className="pe-filter-chip">
                Status: {columnFilters.status}
                <span className="pe-filter-chip-remove" onClick={() => clearSingleFilter('status')}>
                  <FaTimes />
                </span>
              </span>
            )}
            <span className="pe-clear-all-link" onClick={handleClearAllFilters}>
              Clear All
            </span>
          </div>
        )}

        {/* ─── 4. Master Data Table (Properly Aligned Columns, Zero Clutter) ─ */}
        <div className="master-table-container">
          <table className="master-data-table pe-table">
            <thead ref={filterBarRef} className={activePopover ? 'has-open-popover' : ''}>
              <tr>
                {/* 1. Date */}
                <th
                  className={activePopover === 'date' ? 'th-popover-open' : ''}
                  style={{ minWidth: '140px', width: '11%', position: 'relative', zIndex: activePopover === 'date' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${columnFilters.date ? 'active' : ''}`}
                    onClick={() => setActivePopover((prev) => (prev === 'date' ? null : 'date'))}
                  >
                      <div className="th-filter-title">
                        <FaCalendarAlt className="th-icon" />
                        <span>Date</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>
                    {activePopover === 'date' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Entry Date</label>
                        <input
                          type="date"
                          value={columnFilters.date}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({ ...prev, date: e.target.value }))
                          }
                          onClick={(e) => {
                            try {
                              e.target.showPicker();
                            } catch (err) { }
                          }}
                          className="header-filter-input"
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              setColumnFilters((prev) => ({ ...prev, date: '' }));
                              setActivePopover(null);
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActivePopover(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 2. Invoice Date */}
                  <th
                    className={activePopover === 'invoiceDate' ? 'th-popover-open' : ''}
                    style={{ minWidth: '185px', width: '13%', position: 'relative', zIndex: activePopover === 'invoiceDate' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${columnFilters.invoiceDate ? 'active' : ''}`}
                      onClick={() =>
                        setActivePopover((prev) => (prev === 'invoiceDate' ? null : 'invoiceDate'))
                      }
                    >
                      <div className="th-filter-title">
                        <FaCalendarAlt className="th-icon" />
                        <span>Invoice Date</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>
                    {activePopover === 'invoiceDate' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Invoice Date</label>
                        <input
                          type="date"
                          value={columnFilters.invoiceDate}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({ ...prev, invoiceDate: e.target.value }))
                          }
                          onClick={(e) => {
                            try {
                              e.target.showPicker();
                            } catch (err) { }
                          }}
                          className="header-filter-input"
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              setColumnFilters((prev) => ({ ...prev, invoiceDate: '' }));
                              setActivePopover(null);
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActivePopover(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 3. GRN No */}
                  <th
                    className={activePopover === 'grnNo' ? 'th-popover-open' : ''}
                    style={{ minWidth: '145px', width: '11%', position: 'relative', zIndex: activePopover === 'grnNo' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${columnFilters.grnNo ? 'active' : ''}`}
                      onClick={() => setActivePopover((prev) => (prev === 'grnNo' ? null : 'grnNo'))}
                    >
                      <div className="th-filter-title">
                        <FaFileAlt className="th-icon" />
                        <span>GRN No</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>
                    {activePopover === 'grnNo' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by GRN Number</label>
                        <input
                          type="text"
                          placeholder="e.g. GRN-2026-..."
                          value={columnFilters.grnNo}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({ ...prev, grnNo: e.target.value }))
                          }
                          className="header-filter-input"
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              setColumnFilters((prev) => ({ ...prev, grnNo: '' }));
                              setActivePopover(null);
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActivePopover(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 4. Invoice Number */}
                  <th
                    className={activePopover === 'invoiceNumber' ? 'th-popover-open' : ''}
                    style={{ minWidth: '205px', width: '15%', position: 'relative', zIndex: activePopover === 'invoiceNumber' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${columnFilters.invoiceNumber ? 'active' : ''}`}
                      onClick={() =>
                        setActivePopover((prev) =>
                          prev === 'invoiceNumber' ? null : 'invoiceNumber'
                        )
                      }
                    >
                      <div className="th-filter-title">
                        <FaFileInvoice className="th-icon" />
                        <span>Invoice Number</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>
                    {activePopover === 'invoiceNumber' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Invoice Number</label>
                        <input
                          type="text"
                          placeholder="e.g. INV-889..."
                          value={columnFilters.invoiceNumber}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({
                              ...prev,
                              invoiceNumber: e.target.value
                            }))
                          }
                          className="header-filter-input"
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              setColumnFilters((prev) => ({ ...prev, invoiceNumber: '' }));
                              setActivePopover(null);
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActivePopover(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 5. Supplier */}
                  <th
                    className={activePopover === 'supplierName' ? 'th-popover-open' : ''}
                    style={{ minWidth: '165px', width: '16%', position: 'relative', zIndex: activePopover === 'supplierName' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${columnFilters.supplierName ? 'active' : ''}`}
                      onClick={() =>
                        setActivePopover((prev) =>
                          prev === 'supplierName' ? null : 'supplierName'
                        )
                      }
                    >
                      <div className="th-filter-title">
                        <FaTruck className="th-icon" />
                        <span>Supplier</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>
                    {activePopover === 'supplierName' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Supplier</label>
                        <input
                          type="text"
                          placeholder="Search supplier..."
                          value={columnFilters.supplierName}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({
                              ...prev,
                              supplierName: e.target.value
                            }))
                          }
                          className="header-filter-input"
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              setColumnFilters((prev) => ({ ...prev, supplierName: '' }));
                              setActivePopover(null);
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActivePopover(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 6. Payment Amount */}
                  <th style={{ minWidth: '185px', width: '14%' }}>
                    <div className="th-filter-trigger static">
                      <div className="th-filter-title">
                        <FaCoins className="th-icon" />
                        <span>Payment Amount</span>
                      </div>
                    </div>
                  </th>

                  {/* 7. Type */}
                  <th
                    className={activePopover === 'type' ? 'th-popover-open' : ''}
                    style={{ minWidth: '130px', width: '10%', position: 'relative', zIndex: activePopover === 'type' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${columnFilters.type !== 'ALL' ? 'active' : ''}`}
                      onClick={() => setActivePopover((prev) => (prev === 'type' ? null : 'type'))}
                    >
                      <div className="th-filter-title">
                        <FaTag className="th-icon" />
                        <span>Type</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>
                    {activePopover === 'type' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Type</label>
                        <select
                          value={columnFilters.type}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({ ...prev, type: e.target.value }))
                          }
                          className="header-filter-select"
                          autoFocus
                        >
                          <option value="ALL">All Types</option>
                          <option value="PURCHASE_ORDER">Purchase Order</option>
                          <option value="RETURN_ORDER">Return Order</option>
                        </select>
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              setColumnFilters((prev) => ({ ...prev, type: 'ALL' }));
                              setActivePopover(null);
                            }}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActivePopover(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 8. Status */}
                  <th
                    className={activePopover === 'status' ? 'th-popover-open' : ''}
                    style={{ minWidth: '135px', width: '10%', position: 'relative', zIndex: activePopover === 'status' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${columnFilters.status !== 'ALL' ? 'active' : ''}`}
                      onClick={() =>
                        setActivePopover((prev) => (prev === 'status' ? null : 'status'))
                      }
                    >
                      <div className="th-filter-title">
                        <FaFlag className="th-icon" />
                        <span>Status</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>
                    {activePopover === 'status' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Status</label>
                        <select
                          value={columnFilters.status}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({ ...prev, status: e.target.value }))
                          }
                          className="header-filter-select"
                          autoFocus
                        >
                          <option value="ALL">All Status</option>
                          <option value="INVOICED">Invoiced</option>
                          <option value="PROCESSED_AND_RESTOCKED">Processed and Restocked</option>
                          <option value="DELETED">Deleted</option>
                        </select>
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              setColumnFilters((prev) => ({ ...prev, status: 'ALL' }));
                              setActivePopover(null);
                            }}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActivePopover(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="table-state-cell">
                      <div className="table-loading-state">
                        <FaClock size={32} color="#64748b" />
                        <p>Loading Purchase Orders...</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedEntries.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="table-state-cell">
                      <div className="table-empty-state">
                        <FaSearch size={32} color="#94a3b8" />
                        <h4>No Purchase Entries Found</h4>
                        <p>No records match your selected criteria. Try adjusting the date range or create a new stock entry.</p>
                        <button
                          type="button"
                          className="master-new-appt-btn"
                          style={{ marginTop: 12 }}
                          onClick={() => {
                            setEntryToEdit(null);
                            setIsModalOpen(true);
                          }}
                        >
                          <FaPlus size={12} /> Create Stock Entry
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedEntries.map((item) => (
                    <tr
                      key={item.id}
                      className="table-data-row"
                      onClick={() => handleRowClick(item)}
                      title="Click row to manage item details"
                    >
                      {/* Date (e.g. 23 Sep 2026) */}
                      <td>
                        <span className="pe-date-cell">
                          {formatDateReadable(item.entryDate)}
                        </span>
                      </td>

                      {/* Invoice Date (e.g. 22 Sep 2026) */}
                      <td>
                        <span className="pe-date-cell">
                          {formatDateReadable(item.invoiceDate)}
                        </span>
                      </td>

                      {/* GRN No */}
                      <td>
                        <span className="pe-mono-chip grn">{item.grnNo}</span>
                      </td>

                      {/* Invoice Number */}
                      <td>
                        <span className="pe-mono-chip inv">{item.invoiceNumber || '—'}</span>
                      </td>

                      {/* Supplier */}
                      <td>
                        <span className="pe-supplier-name" title={item.supplierName}>
                          {item.supplierName || '—'}
                        </span>
                      </td>

                      {/* Payment Amount */}
                      <td>
                        <div className="pe-amount-cell">
                          <span className="pe-main-amt">{formatCurrency(item.amount)}</span>
                        </div>
                      </td>

                      {/* Type Badge */}
                      <td>
                        <span className={`pe-type-badge ${item.entryType === 'RETURN_ORDER' ? 'type-return-order' : 'type-purchase-order'}`}>
                          {item.entryType === 'RETURN_ORDER' ? 'Return Order' : 'Purchase Order'}
                        </span>
                      </td>

                      {/* Status Badge */}
                      <td>
                        <span
                          className={`pe-status-pill status-${String(item.status || 'INVOICED').toLowerCase().replace(/\s+/g, '_')}`}
                        >
                          {(item.status === 'PROCESSED_AND_RESTOCKED' || item.status === 'PROCESSED' || item.status === 'RESTOCKED' || item.status === 'PROCESSED AND RESTOCKED') ? (
                            <>
                              <span>PROCESSED AND</span>
                              <span>RESTOCKED</span>
                            </>
                          ) : (
                            item.statusDisplay || (
                              item.status === 'INVOICED' ? 'Invoiced' :
                                item.status === 'DELETED' ? 'Deleted' : item.status
                            )
                          )}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </div>

        {/* ─── 5. Master Pagination Footer ─────────────────────────────────── */}
        {!loading && entries.length > 0 && (
          <div className="master-pagination-footer">
            <div className="pagination-count">
              Showing page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> (
              <strong>{entries.length}</strong> purchase entries)
            </div>

            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, val) => setCurrentPage(val)}
              color="primary"
              size="small"
              shape="rounded"
            />
          </div>
        )}
      </div>

      {/* ─── 6. Create / Edit Stock Entry Modal ───────────────────────────── */}
      <CreateStockEntryModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEntryToEdit(null);
        }}
        onSuccess={(savedEntry) => {
          setIsModalOpen(false);
          setEntryToEdit(null);
          fetchPurchaseData();
          if (savedEntry) {
            const poNumber = savedEntry.grnNo || savedEntry.id;
            setSelectedPurchaseOrder(savedEntry);
            navigate(`/pharmacy/purchase-entries/${encodeURIComponent(poNumber)}`, {
              state: { order: savedEntry }
            });
          }
        }}
        initialData={entryToEdit}
        suppliers={suppliers}
        defaultType={activeSubTab}
        onAddNewSupplierSuccess={(newSup) => {
          setSuppliers((prev) => [newSup, ...prev]);
        }}
      />
    </div>
  );
};

export default PurchaseEntries;
